import './App.css';
import React, { useState } from 'react';
import originals from "./static/originals.json";
import masters from "./static/masters.json";
import { Stack, Typography, Container, useMediaQuery, useTheme } from '@mui/material';
import PaintingGallery from './card/PaintingGallery.tsx';
import { CustomThemeProvider } from './components/ThemeProvider.tsx';
import { GalleryAppBar } from './components/AppBar.tsx';
import ContactForm from './components/ContactForm.tsx';

function AppContent() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md')); // md and up for desktop
  
  // Mobile: 3 columns, 0.5x text; Desktop: 5 columns, 0.5x text
  const [columns, setColumns] = useState(isDesktop ? 5 : 3);
  const [textSize, setTextSize] = useState(0.5);

  // Function to group masters by artist categories
  const groupMastersByArtist = (paintings) => {
    const groups = {
      'Van Eyck, Leonardo da Vinci, Botticelli, Michelangelo, Rafael': [],
      'Titian, Rubens, Rembrandt, Van Dyck': [],
      'More': []
    };

    paintings.forEach(painting => {
      if (painting.name.includes('Van Eyck') ||
          painting.name.includes('Leonardo da Vinci') ||
          painting.name.includes('Botticelli') ||
          painting.name.includes('Michelangelo') ||
          painting.name.includes('Rafael')) {
        groups['Van Eyck, Leonardo da Vinci, Botticelli, Michelangelo, Rafael'].push(painting);
      } else if (painting.name.includes('Titian') ||
                 painting.name.includes('Rubens') ||
                 painting.name.includes('Rembrandt') ||
                 painting.name.includes('Anthony van Dyck')) {
        groups['Titian, Rubens, Rembrandt, Van Dyck'].push(painting);
      } else {
        groups['More'].push(painting);
      }
    });

    return groups;
  };

  // Function to group originals by category
  const groupOriginalsByCategory = (paintings) => {
    const groups = {
      'Portraits': [],
      'Still Life and Landscapes': []
    };

    paintings.forEach(painting => {
      // Categorize based on painting name and description
      if (painting.name.includes('Rose') ||
          painting.name.includes('Apples') ||
          painting.name.includes('Monet') ||
          painting.name.includes('Vancouver') ||
          painting.name.includes('Berries')) {
        groups['Still Life and Landscapes'].push(painting);
      } else {
        // Default to Portraits for bride, princess, lady, etc.
        groups['Portraits'].push(painting);
      }
    });

    return groups;
  };

  const masterGroups = groupMastersByArtist(masters);
  const originalGroups = groupOriginalsByCategory(originals);

  return (
    <div className="App">
      <GalleryAppBar
        columns={columns}
        textSize={textSize}
        onColumnsChange={setColumns}
        onTextSizeChange={setTextSize}
      />
      
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Stack spacing={3}>
          <Stack sx={{ margin: "1vw" }} spacing="1vw" alignItems="center">
            <Typography
              fontFamily="Playfair Display"
              fontSize={`${5 * textSize}vw`}
              color="text.primary"
              sx={{ textAlign: 'center' }}
            >
              Shabnam Jabeen
            </Typography>
            <img
              src="/images/signature-clean.png"
              alt="Shabnam Jabeen's signature"
              style={{
                maxWidth: `${20 * textSize}vw`,
                height: 'auto',
                opacity: 0.8
              }}
            />
          </Stack>
    
          <Stack spacing="4vw">
            {/* My Originals Introduction */}
            {/* <Stack sx={{ margin: "1vw" }} spacing="0.1vw">
              <Typography
                fontFamily="Playfair Display"
                fontSize={`${5 * textSize}vw`}
                marginBottom="1vw"
                color="text.primary"
              >
                My Originals
              </Typography>
              <Typography
                align="left"
                fontFamily="Bitter"
                fontStyle="italic"
                fontSize={`${2 * textSize}vw`}
                color="text.secondary"
                marginBottom="2vw"
              >
                A showcase of original paintings exploring various subjects and styles
              </Typography>
            </Stack> */}

            {/* Portraits Group */}
            {originalGroups['Portraits'].length > 0 && (
              <Stack spacing={2}>
                <Typography
                  fontFamily="Playfair Display"
                  fontSize={`${Math.max(1.6, 2.4 * textSize)}rem`}
                  color="text.primary"
                  sx={{ textAlign: 'left', ml: 2 }}
                >
                  Portraits
                </Typography>
                <PaintingGallery
                  paintings={originalGroups['Portraits']}
                  columns={columns}
                  rows={3}
                  textSize={textSize}
                />
              </Stack>
            )}

            {/* Still Life and Landscapes Group */}
            {originalGroups['Still Life and Landscapes'].length > 0 && (
              <Stack spacing={2}>
                <Typography
                  fontFamily="Playfair Display"
                  fontSize={`${Math.max(1.6, 2.4 * textSize)}rem`}
                  color="text.primary"
                  sx={{ textAlign: 'left', ml: 2 }}
                >
                  Still Life and Landscapes
                </Typography>
                <PaintingGallery
                  paintings={originalGroups['Still Life and Landscapes']}
                  columns={columns}
                  rows={3}
                  textSize={textSize}
                />
              </Stack>
            )}

            {/* My Masters Introduction */}
            <Stack sx={{ margin: "1vw" }} spacing="0.1vw">
              <Typography
                fontFamily="Playfair Display"
                fontSize={`${Math.max(1.6, 2.4 * textSize)}rem`}
                marginBottom="1vw"
                color="text.primary"
              >
                My Masters
              </Typography>
              <Typography
                align="left"
                fontFamily="Bitter"
                fontStyle="italic"
                fontSize={`${Math.min(2 * textSize, 2.5)}vw`}
                color="text.secondary"
                marginBottom="2vw"
              >
                Without any professional training in arts but with a deep love for painting, I proudly present My Masters - a collage of paintings after some of the artists who inspire me the most. With each attempt at replicating their masterpieces, I am humbled by their unparalleled talents and the vastness of their creative achievements.
              </Typography>
            </Stack>

            {/* Van Eyck, Leonardo da Vinci, Botticelli, Michelangelo, Rafael Group */}
            {masterGroups['Van Eyck, Leonardo da Vinci, Botticelli, Michelangelo, Rafael'].length > 0 && (
              <Stack spacing={2}>
                <Typography
                  fontFamily="Playfair Display"
                  fontSize={`${Math.max(1.6, 2.4 * textSize)}rem`}
                  color="text.primary"
                  sx={{ textAlign: 'left', ml: 2 }}
                >
                  Van Eyck, Leonardo da Vinci, Botticelli, Michelangelo, Rafael
                </Typography>
                <PaintingGallery
                  paintings={masterGroups['Van Eyck, Leonardo da Vinci, Botticelli, Michelangelo, Rafael']}
                  columns={columns}
                  rows={3}
                  textSize={textSize}
                />
              </Stack>
            )}

            {/* Titian, Rubens, Rembrandt, Van Dyck Group */}
            {masterGroups['Titian, Rubens, Rembrandt, Van Dyck'].length > 0 && (
              <Stack spacing={2}>
                <Typography
                  fontFamily="Playfair Display"
                  fontSize={`${Math.max(1.6, 2.4 * textSize)}rem`}
                  color="text.primary"
                  sx={{ textAlign: 'left', ml: 2 }}
                >
                  Titian, Rubens, Rembrandt, Van Dyck
                </Typography>
                <PaintingGallery
                  paintings={masterGroups['Titian, Rubens, Rembrandt, Van Dyck']}
                  columns={columns}
                  rows={3}
                  textSize={textSize}
                />
              </Stack>
            )}

            {/* More Group */}
            {masterGroups['More'].length > 0 && (
              <Stack spacing={2}>
                <Typography
                  fontFamily="Playfair Display"
                  fontSize={`${Math.max(1.6, 2.4 * textSize)}rem`}
                  color="text.primary"
                  sx={{ textAlign: 'left', ml: 2 }}
                >
                  More
                </Typography>
                <PaintingGallery
                  paintings={masterGroups['More']}
                  columns={columns}
                  rows={3}
                  textSize={textSize}
                />
              </Stack>
            )}

            {/* Contact Form Section */}
            <Stack
              id="contact-form"
              spacing={2}
              sx={{ mt: 8, pt: 4, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}
            >
              <ContactForm textSize={textSize} />
            </Stack>
            
            <br/><br/><br/>
          </Stack>
        </Stack>
      </Container>
    </div>
  );
}

function App() {
  return (
    <CustomThemeProvider>
      <AppContent />
    </CustomThemeProvider>
  );
}

export default App;
